import React from 'react';

export type FmTimelineKind = 'past' | 'current' | 'future';

export interface FmTimelineItem {
  id?: string;
  year: string;
  title: React.ReactNode;
  meta?: React.ReactNode;
  kind: FmTimelineKind;
  /**
   * Accessible, localised label for the temporal phase (past / current /
   * future). Rendered as a visible chip so the past/current/future distinction
   * does NOT rely on color alone (WCAG 1.4.1). When omitted the chip is not
   * rendered (back-compat for legacy callers).
   */
  kindLabel?: string;
  marker?: React.ReactNode;
  accentColor?: string;
  content?: React.ReactNode;
  /** Optional stable test id for the row (overrides the shared itemTestId). */
  testId?: string;
}

export interface FmTimelineProps {
  items: FmTimelineItem[];
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  testId?: string;
  itemTestId?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/** Vertical historical timeline with past/current/future visual semantics. */
export const FmTimeline: React.FC<FmTimelineProps> = ({
  items,
  className,
  testId,
  itemTestId,
}) => (
  <ol className={cx('timeline', className)} data-testid={testId}>
    {items.map((item, index) => {
      const itemStyle = item.accentColor
        ? ({ '--tl-accent': item.accentColor } as React.CSSProperties)
        : undefined;

      return (
        <li
          key={item.id ?? `${item.year}-${index}`}
          className={cx('tl-item', `tl-item--${item.kind}`)}
          data-testid={item.testId ?? itemTestId}
          data-kind={item.kind}
          style={itemStyle}
        >
          <span className={cx('tl-dot', Boolean(item.marker) && 'tl-dot--marker')} aria-hidden="true">
            {item.marker}
          </span>
          <div className="tl-head">
            <span className="tl-year">{item.year}</span>
            {item.kindLabel ? (
              <span className={cx('tl-phase', `tl-phase--${item.kind}`)}>
                {item.kindLabel}
              </span>
            ) : null}
          </div>
          <div className="tl-title">{item.title}</div>
          {item.meta ? <div className="tl-meta">{item.meta}</div> : null}
          {item.content ? <div className="tl-content">{item.content}</div> : null}
        </li>
      );
    })}
  </ol>
);

export default FmTimeline;
